import { useState } from 'react';

/**
 * Fold — a labelled section that collapses.
 *
 * Sky-Drop explains itself with reference material: a briefing on the landing
 * screen, two tables of bindings and a list of tips before a match. On a
 * desktop window that is a page you can take in at a glance. On a phone it is
 * a wall — the pre-fight panel ran to roughly three screenfuls of scrolling
 * before the button that starts the fight, which is a lot to hand somebody who
 * only wants to play.
 *
 * So each block folds. Built on <details>/<summary> rather than on state and a
 * click handler: the browser already does the toggling, the keyboard support
 * and the screen-reader semantics, and doing it by hand would be reimplementing
 * all three worse.
 *
 * @param {object} props
 * @param {string} props.label      The summary line, always visible.
 * @param {boolean} [props.defaultOpen] Open on first render. Uncontrolled after
 *   that — the browser owns the state, so a re-render never slams it shut
 *   under someone's finger.
 * @param {string} [props.className] Extra classes on the <details>.
 */
const Fold = ({ label, defaultOpen = false, className = '', children }) => (
  <details className={`fold ${className}`.trim()} open={defaultOpen}>
    <summary className="fold-head">{label}</summary>
    <div className="fold-body">{children}</div>
  </details>
);

/**
 * Has this screen got room to show everything at once?
 *
 * What folds away on a phone should not be hidden from a desktop window that
 * was showing it comfortably — collapsing there would be tidying away something
 * nobody asked to have tidied. So the folds start open when there is space and
 * closed when there is not, and after that the reader decides.
 *
 * Both axes matter, which is why this is not a width breakpoint: a phone turned
 * sideways is wider than the panel and has almost no height, and it is the
 * height that the tables and tips actually consume.
 *
 * Read once, at mount, rather than subscribed to: this decides a starting
 * state, and a device rotated mid-read should not have its sections opened or
 * shut underneath it.
 *
 * @returns {boolean}
 */
export function useRoomyScreen() {
  const [roomy] = useState(() => (
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(min-width: 700px) and (min-height: 620px)').matches
      : true
  ));
  return roomy;
}

export default Fold;
